// DiagiaSidebar — the left sidebar from the mockup.
//
// Three groups:
//   ACCUEIL         — Home
//   OUTIL           — Nouveau diagnostic, Mes diagnostics
//   ADMINISTRATION  — Campagnes, Participants, Résultats, Paramètres
//                     (admin only — gated by the `isAdmin` prop)
//
// Bottom: user identity chip.

import React from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaHome,
  FaPlusCircle,
  FaFileAlt,
  FaBullhorn,
  FaUsers,
  FaChartPie,
  FaCog,
  FaEnvelopeOpenText,
} from 'react-icons/fa';

export default function DiagiaSidebar({ identity, pendingInvitations = 0 }) {
  const { t } = useTranslation();

  const NAV_TOOL = [
    { to: '/diagia/invitations', icon: <FaEnvelopeOpenText />, label: t('diagia.nav.invitations', 'Mes invitations'), showBadge: true },
    { to: '/diagia/new',         icon: <FaPlusCircle />,       label: t('diagia.nav.new', 'Nouveau diagnostic') },
    { to: '/diagia/diagnostics', icon: <FaFileAlt />,          label: t('diagia.nav.mine', 'Mes diagnostics') },
  ];

  const NAV_ADMIN = [
    { to: '/diagia/campaigns',    icon: <FaBullhorn />, label: t('diagia.nav.campaigns', 'Campagnes'),      enabled: true },
    { to: '/diagia/participants', icon: <FaUsers />,    label: t('diagia.nav.participants', 'Participants'), enabled: true },
    { to: '/diagia/results',      icon: <FaChartPie />, label: t('diagia.nav.results', 'Résultats'),         enabled: true },
    { to: '/diagia/settings',     icon: <FaCog />,      label: t('diagia.nav.settings', 'Paramètres'),       enabled: false },
  ];

  const isAdmin = identity?.is_admin === true;
  const initials = (identity?.label || t('diagia.guest', 'Invité'))
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <aside className="dia-sidebar">
      <div className="dia-brand">
        <span className="dia-brand-mark">Diag</span>
        <span className="dia-brand-accent">IA</span>
      </div>

      <nav className="dia-nav">
        <div className="dia-nav-section">
          <span className="dia-nav-heading">{t('diagia.nav.home_section', 'Accueil')}</span>
          <NavLink
            to="/diagia"
            end
            className={({ isActive }) =>
              `dia-nav-link${isActive ? ' is-active' : ''}`
            }
          >
            <FaHome /> {t('diagia.nav.home', 'Accueil')}
          </NavLink>
        </div>

        <div className="dia-nav-section">
          <span className="dia-nav-heading">{t('diagia.nav.tool_section', 'Outil')}</span>
          {NAV_TOOL.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `dia-nav-link${isActive ? ' is-active' : ''}`
              }
            >
              {item.icon} {item.label}
              {item.showBadge && pendingInvitations > 0 && (
                <span className="dia-nav-badge">{pendingInvitations}</span>
              )}
            </NavLink>
          ))}
        </div>

        {isAdmin && (
          <div className="dia-nav-section">
            <span className="dia-nav-heading">{t('diagia.nav.admin_section', 'Administration')}</span>
            {NAV_ADMIN.map((item) =>
              item.enabled ? (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `dia-nav-link${isActive ? ' is-active' : ''}`
                  }
                >
                  {item.icon} {item.label}
                </NavLink>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className="dia-nav-link dia-nav-disabled"
                  title={t('diagia.nav.next_phase', 'Disponible à la prochaine phase')}
                  onClick={(e) => e.preventDefault()}
                >
                  {item.icon} {item.label}
                  <span className="dia-nav-tag">{t('diagia.nav.soon', 'bientôt')}</span>
                </NavLink>
              )
            )}
          </div>
        )}
      </nav>

      <div className="dia-user">
        <div className="dia-user-avatar">{initials || 'IA'}</div>
        <div className="dia-user-meta">
          <span className="dia-user-name">{identity?.label || t('diagia.guest', 'Invité')}</span>
          <span className="dia-user-role">
            {isAdmin
              ? t('diagia.role_admin', 'Administrateur')
              : identity?.authenticated
                ? t('diagia.role_participant', 'Participant')
                : t('diagia.role_anonymous', 'Non connecté')}
          </span>
        </div>
      </div>
    </aside>
  );
}
